import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlayCircle,
  Search,
  Clock,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useLanguage } from '../contexts/LanguageContext';

const VIDEOS_PER_PAGE = 12;

/**
 * Videos – YouTube video gallery with inline player and related cards.
 */
export default function Videos() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(VIDEOS_PER_PAGE);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [configured, setConfigured] = useState(true);

  // ── Fetch videos ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch('/api/youtube/videos')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setVideos(data.videos || []);
          setConfigured(data.configured !== false);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to fetch videos:', err);
          setError(err.message);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // ── Filter by search query ───────────────────────────────
  const filteredVideos = useMemo(() => {
    if (!searchQuery.trim()) return videos;
    const q = searchQuery.toLowerCase();
    return videos.filter(
      (v) =>
        v.title?.toLowerCase().includes(q) ||
        v.description?.toLowerCase().includes(q)
    );
  }, [videos, searchQuery]);

  const displayedVideos = filteredVideos.slice(0, visibleCount);
  const hasMore = visibleCount < filteredVideos.length;

  // ── Handlers ─────────────────────────────────────────────
  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => prev + VIDEOS_PER_PAGE);
  }, []);

  const handleVideoClick = useCallback((video) => {
    setSelectedVideo(video);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setSelectedVideo(null);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const truncateDescription = (desc, maxLen = 120) => {
    if (!desc) return '';
    if (desc.length <= maxLen) return desc;
    return desc.slice(0, maxLen).replace(/\s+\S*$/, '') + '…';
  };

  // Reset visible count when search changes
  useEffect(() => {
    setVisibleCount(VIDEOS_PER_PAGE);
  }, [searchQuery]);

  return (
    <div className="min-h-[calc(100vh-48px)] bg-gradient-to-b from-background to-muted/30">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="max-w-5xl mx-auto px-4 py-10 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <PlayCircle className="h-8 w-8" />
            <h1
              className="text-3xl md:text-4xl font-bold tracking-tight uppercase"
              style={{ fontFamily: 'Lato, Arial, sans-serif' }}
            >
              {t('videos.title') || 'Videos'}
            </h1>
          </div>
          <p className="text-white/70 max-w-xl mx-auto">
            {t('videos.description') || 'Deck profiles, set reviews, and gameplay videos from our YouTube channel.'}
          </p>

          {/* Search */}
          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-6 max-w-md mx-auto relative"
          >
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
            <Input
              placeholder={t('videos.searchPlaceholder') || 'Search videos…'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/50 focus-visible:ring-white/30"
            />
          </form>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Stats row */}
        {!loading && !error && configured && filteredVideos.length > 0 && (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <PlayCircle className="h-4 w-4" />
              <span>
                {filteredVideos.length}{' '}
                {filteredVideos.length === 1 ? 'video' : 'videos'}
                {searchQuery && ` matching "${searchQuery}"`}
              </span>
            </div>
            <Separator className="mb-6" />
          </>
        )}

        {/* Loading state */}
        {loading && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse overflow-hidden">
                <div className="aspect-video bg-muted" />
                <CardContent className="pt-4 space-y-2">
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="text-center py-16 text-muted-foreground">
            <PlayCircle className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg mb-2">Failed to load videos</p>
            <p className="text-sm">{error}</p>
            <Button
              variant="link"
              className="mt-2"
              onClick={() => window.location.reload()}
            >
              Try again
            </Button>
          </div>
        )}

        {/* Unconfigured state — YouTube API key not set */}
        {!loading && !error && !configured && (
          <div className="text-center py-16 text-muted-foreground">
            <PlayCircle className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg">{t('videos.comingSoon') || 'Videos coming soon!'}</p>
            <p className="text-sm mt-1">{t('videos.comingSoonDescription') || 'Check back later for deck profiles, set reviews, and gameplay content.'}</p>
          </div>
        )}

        {/* Empty search state — API is configured but no results for query */}
        {!loading && !error && configured && filteredVideos.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <PlayCircle className="h-12 w-12 mx-auto mb-4 opacity-40" />
            <p className="text-lg">{t('videos.noVideos') || 'No videos found.'}</p>
            {searchQuery && (
              <Button variant="link" onClick={() => setSearchQuery('')}>
                {t('videos.clearSearch') || 'Clear search'}
              </Button>
            )}
          </div>
        )}

        {/* Video grid */}
        {!loading && !error && displayedVideos.length > 0 && (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {displayedVideos.map((video) => (
                <VideoCard
                  key={video.videoId}
                  video={video}
                  onClick={() => handleVideoClick(video)}
                  onCardClick={(cardNumber) => navigate(`/gallery?search=${cardNumber}`)}
                  formatDate={formatDate}
                  truncateDescription={truncateDescription}
                />
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button variant="outline" onClick={handleLoadMore}>
                  {t('videos.loadMore') || 'Load More Videos'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Video Player Dialog ─────────────────────────────── */}
      <Dialog open={!!selectedVideo} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-4xl w-[95vw] p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-base font-semibold line-clamp-1 pr-8">
              {selectedVideo?.title}
            </DialogTitle>
          </DialogHeader>

          {/* Responsive 16:9 iframe */}
          <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
            {selectedVideo && (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1&rel=0`}
                title={selectedVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            )}
          </div>

          {/* Description + Related Cards */}
          <div className="p-4 pt-3 space-y-3 max-h-60 overflow-y-auto">
            {selectedVideo?.publishedAt && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {formatDate(selectedVideo.publishedAt)}
              </div>
            )}

            {selectedVideo?.description && (
              <p className="text-sm text-muted-foreground whitespace-pre-line line-clamp-4">
                {selectedVideo.description}
              </p>
            )}

            {/* Related cards row */}
            {selectedVideo?.relatedCards?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
                  {t('videos.cardsMentioned') || 'Cards mentioned'}
                </h4>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {selectedVideo.relatedCards.slice(0, 8).map((card) => (
                    <button
                      key={card.number}
                      onClick={() => {
                        handleCloseDialog();
                        navigate(`/gallery?search=${card.number}`);
                      }}
                      className="flex-shrink-0 group"
                      title={`${card.number} – ${card.name}`}
                    >
                      {card.thumbnail ? (
                        <img
                          src={`/api/image?url=${encodeURIComponent(card.thumbnail)}`}
                          alt={card.name}
                          className="h-20 w-auto rounded border border-border group-hover:border-primary transition-colors"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-20 w-14 rounded border border-border bg-muted flex items-center justify-center text-[10px] text-muted-foreground">
                          {card.number}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* YouTube link */}
            {selectedVideo && (
              <a
                href={`https://www.youtube.com/watch?v=${selectedVideo.videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                {t('videos.watchOnYouTube') || 'Watch on YouTube'} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Individual video card with thumbnail, title, description, and related cards.
 */
function VideoCard({ video, onClick, onCardClick, formatDate, truncateDescription }) {
  return (
    <Card
      className="group overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5"
      onClick={onClick}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted overflow-hidden">
        {video.thumbnail ? (
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <PlayCircle className="h-10 w-10 text-primary/30" />
          </div>
        )}

        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
          <PlayCircle className="h-12 w-12 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
        </div>
      </div>

      <CardContent className="pt-4 space-y-2">
        <h2 className="text-base font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
          {video.title}
        </h2>

        {video.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {truncateDescription(video.description)}
          </p>
        )}

        {/* Meta row */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
          {video.publishedAt && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDate(video.publishedAt)}
            </span>
          )}
          {video.relatedCards?.length > 0 && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {video.relatedCards.length} card{video.relatedCards.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {/* Related card thumbnails row (max 5) */}
        {video.relatedCards?.length > 0 && (
          <div className="flex gap-1.5 pt-1">
            {video.relatedCards.slice(0, 5).map((card) => (
              <button
                key={card.number}
                onClick={(e) => {
                  e.stopPropagation();
                  onCardClick(card.number);
                }}
                className="flex-shrink-0 group/card"
                title={`${card.number} – ${card.name}`}
              >
                {card.thumbnail ? (
                  <img
                    src={`/api/image?url=${encodeURIComponent(card.thumbnail)}`}
                    alt={card.name}
                    className="h-12 w-auto rounded border border-border group-hover/card:border-primary transition-colors"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-12 w-8 rounded border border-border bg-muted flex items-center justify-center text-[8px] text-muted-foreground">
                    {card.number}
                  </div>
                )}
              </button>
            ))}
            {video.relatedCards.length > 5 && (
              <span className="flex items-center text-[10px] text-muted-foreground pl-1">
                +{video.relatedCards.length - 5}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center text-xs text-primary font-medium pt-1 group-hover:gap-1.5 transition-all">
          Watch <ChevronRight className="h-3 w-3" />
        </div>
      </CardContent>
    </Card>
  );
}
